/**
 * API client for Dosetronic dosing events endpoints
 */

import type {
  DosingEventResponse,
  ManualDosingRequest,
  ManualDosingUpdateRequest,
  ManualDosingResponse,
  DosingChemicalsResponse,
  DosingScheduleListResponse,
  DosingScheduleCreateRequest,
  DosingScheduleUpdateRequest,
  DosingSchedule,
} from '@/types/dosing';
import { buildApiUrl } from './config';
import { apiClient } from './client';

export interface DosingEventFilters {
  start_date?: string; // YYYY-MM-DD
  end_date?: string; // YYYY-MM-DD
  pump_number?: number; // 1-5
}

export const api = {
  /**
   * Fetch dosing events with optional filtering
   */
  async getDosingEvents(tankId: string, filters?: DosingEventFilters): Promise<DosingEventResponse> {
    const url = new URL(buildApiUrl(`/api/tanks/${tankId}/dosing/events`), window.location.origin);

    // Add query parameters
    if (filters?.start_date) {
      url.searchParams.append('start_date', filters.start_date);
    }
    if (filters?.end_date) {
      url.searchParams.append('end_date', filters.end_date);
    }
    if (filters?.pump_number !== undefined) {
      url.searchParams.append('pump_number', String(filters.pump_number));
    }

    const response = await apiClient(url.toString(), {
      method: 'GET',
      headers: {
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Create a manual dosing event
   */
  async createManualDosingEvent(
    tankId: string,
    data: ManualDosingRequest
  ): Promise<ManualDosingResponse> {
    const url = buildApiUrl(`/api/tanks/${tankId}/dosing/manual`);

    const response = await apiClient(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Delete a manual dosing event
   */
  async deleteManualDosingEvent(tankId: string, eventId: number): Promise<void> {
    const url = buildApiUrl(`/api/tanks/${tankId}/dosing/manual/${eventId}`);

    const response = await apiClient(url, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
    }
  },

  /**
   * Toggle exclusion status of a dosing event
   */
  async toggleDosingExclusion(
    tankId: string,
    eventId: number,
    excluded: boolean
  ): Promise<{ success: boolean; message: string }> {
    const url = buildApiUrl(`/api/tanks/${tankId}/dosing/events/${eventId}/exclude`);

    const response = await apiClient(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ excluded }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Update a manual dosing event
   */
  async updateManualDosingEvent(
    tankId: string,
    eventId: number,
    data: ManualDosingUpdateRequest
  ): Promise<ManualDosingResponse> {
    const url = buildApiUrl(`/api/tanks/${tankId}/dosing/manual/${eventId}`);

    const response = await apiClient(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Get list of dosing chemicals for autocomplete
   */
  async getDosingChemicals(): Promise<DosingChemicalsResponse> {
    const url = buildApiUrl('/api/dosing/chemicals');

    const response = await apiClient(url, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  },

  // ========================================================================
  // Dosing Schedules
  // ========================================================================

  /**
   * List dosing schedules for a tank
   */
  async getDosingSchedules(
    tankId: string,
    isActive?: boolean,
  ): Promise<DosingScheduleListResponse> {
    const url = new URL(
      buildApiUrl(`/api/tanks/${tankId}/dosing/schedules`),
      window.location.origin,
    );
    if (isActive !== undefined) {
      url.searchParams.append('is_active', String(isActive));
    }

    const response = await apiClient(url.toString(), { method: 'GET' });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * Create a dosing schedule
   */
  async createDosingSchedule(
    tankId: string,
    data: DosingScheduleCreateRequest,
  ): Promise<DosingSchedule> {
    const url = buildApiUrl(`/api/tanks/${tankId}/dosing/schedules`);
    const response = await apiClient(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * Update a dosing schedule (partial update)
   */
  async updateDosingSchedule(
    tankId: string,
    scheduleId: string,
    data: DosingScheduleUpdateRequest,
  ): Promise<DosingSchedule> {
    const url = buildApiUrl(`/api/tanks/${tankId}/dosing/schedules/${scheduleId}`);
    const response = await apiClient(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * Delete a dosing schedule
   */
  async deleteDosingSchedule(
    tankId: string,
    scheduleId: string,
    deleteEvents: boolean = false,
  ): Promise<void> {
    const url = new URL(
      buildApiUrl(`/api/tanks/${tankId}/dosing/schedules/${scheduleId}`),
      window.location.origin,
    );
    if (deleteEvents) {
      url.searchParams.append('delete_events', 'true');
    }
    const response = await apiClient(url.toString(), { method: 'DELETE' });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
    }
  },
};
